/**
 * Ponte fra l'app web e i widget nativi.
 *
 * Su iOS/Android il plugin `LumenWidgets` scrive lo snapshot nello spazio
 * condiviso (App Group / SharedPreferences) e forza il ricaricamento dei
 * widget. Sul web (anteprima, browser) tutto diventa un no-op silenzioso e
 * lo snapshot resta comunque in localStorage per i widget interni all'app.
 */
import {
  parseSnapshot,
  serializeSnapshot,
  snapshotsEqual,
  type WidgetSnapshot,
} from "@/lib/widget-data";
import { supabase } from "@/integrations/supabase/client";

export const WIDGET_SNAPSHOT_KEY = "lumen.widget-snapshot.v1";
/** App Group iOS / nome delle SharedPreferences Android. */
export const WIDGET_SHARED_GROUP = "group.ltd.lumenai.app";

type LumenWidgetsPlugin = {
  setSnapshot(options: { group: string; key: string; json: string }): Promise<void>;
  reload(options?: { kind?: string }): Promise<void>;
};

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  Plugins?: Record<string, unknown>;
  registerPlugin?: <T>(name: string, impl?: unknown) => T;
};

function capacitor(): CapacitorGlobal | undefined {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function isNativeApp(): boolean {
  return Boolean(capacitor()?.isNativePlatform?.());
}

function plugin(): LumenWidgetsPlugin | null {
  const cap = capacitor();
  if (!cap?.isNativePlatform?.()) return null;
  const direct = cap.Plugins?.["LumenWidgets"] as LumenWidgetsPlugin | undefined;
  if (direct) return direct;
  try {
    return cap.registerPlugin?.<LumenWidgetsPlugin>("LumenWidgets") ?? null;
  } catch {
    return null;
  }
}

function readLocal(): WidgetSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    return parseSnapshot(window.localStorage.getItem(WIDGET_SNAPSHOT_KEY));
  } catch {
    return null;
  }
}

/** Ultimo snapshot salvato (usato dai widget interni all'app e come cache). */
export function getCachedSnapshot(): WidgetSnapshot | null {
  return readLocal();
}

/**
 * Salva lo snapshot e ricarica i widget nativi.
 * Non fa nulla se i dati non sono cambiati, per non svegliare i widget invano.
 */
export async function pushSnapshot(snapshot: WidgetSnapshot, options?: { force?: boolean }): Promise<boolean> {
  const previous = readLocal();
  if (!options?.force && snapshotsEqual(previous, snapshot)) return false;

  const json = serializeSnapshot(snapshot);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(WIDGET_SNAPSHOT_KEY, json);
    } catch {
      /* quota piena: i widget nativi ricevono comunque il dato qui sotto */
    }
  }

  const api = plugin();
  if (!api) return true;
  try {
    await api.setSnapshot({ group: WIDGET_SHARED_GROUP, key: WIDGET_SNAPSHOT_KEY, json });
    await api.reload();
  } catch (error) {
    console.error("LumenWidgets bridge failed", error);
  }
  return true;
}

/** Forza solo il ricaricamento dei widget (es. dopo un cambio lingua). */
export async function reloadWidgets(): Promise<void> {
  const api = plugin();
  if (!api) return;
  try {
    await api.reload();
  } catch (error) {
    console.error("LumenWidgets reload failed", error);
  }
}

/** Chiave dello spazio condiviso in cui il nativo trova il token di sessione. */
export const WIDGET_AUTH_KEY = "lumen.widget-auth.v1";

/**
 * Pubblica per il codice nativo l'indirizzo da chiamare e il token della
 * sessione corrente, così i widget possono rinfrescare i dati anche ad app
 * chiusa (refresh in background iOS / WorkManager Android).
 */
export async function publishAuthForWidgets(): Promise<void> {
  const api = plugin();
  if (!api) return;
  try {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session) return;
    const payload = JSON.stringify({
      url: `${import.meta.env['VITE_SUPABASE_URL'] ?? ""}/rest/v1`,
      apikey: import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'] ?? "",
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      expiresAt: session.expires_at ?? null,
      userId: session.user.id,
    });
    await api.setSnapshot({ group: WIDGET_SHARED_GROUP, key: WIDGET_AUTH_KEY, json: payload });
  } catch (error) {
    console.error("LumenWidgets auth publish failed", error);
  }
}
